import React, { useId, useMemo, useRef, useState, useCallback } from 'react';

const GOLD = '#c8a84b';
const GROOVE_OFF = '#1a1a1a';
const DISC_GOLD_BG = '#1a1200';

const SIZE_PX = { sm: 36, md: 60, lg: 90 };

/** Radios de los 5 surcos (viewBox 0–100), del centro al exterior */
const GROOVE_R = [14, 22, 30, 38, 46];
const STROKE = 4.2;
const HOLE_R = 4.8;

const QUALITY = ['', 'Flojo', 'Regular', 'Bueno', 'Muy bueno', 'Disco de Oro'];

/**
 * Convierte distancia al centro (coords viewBox) en puntuación 1–5, o null (agujero / fuera).
 */
function distanceToScore(dist) {
  if (dist == null || Number.isNaN(dist)) return null;
  if (dist < HOLE_R + 2) return null;
  if (dist < 17) return 1;
  if (dist < 25) return 2;
  if (dist < 32.5) return 3;
  if (dist < 40.5) return 4;
  if (dist <= 50) return 5;
  return null;
}

function clientToScore(svgEl, clientX, clientY) {
  if (!svgEl) return null;
  const pt = svgEl.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return null;
  const loc = pt.matrixTransform(ctm.inverse());
  const dx = loc.x - 50;
  const dy = loc.y - 50;
  return distanceToScore(Math.hypot(dx, dy));
}

/**
 * Valoración por “surcos” de vinilo (1–5). Reutilizable para lugares, eventos y transacciones.
 *
 * @param {number} value — 0 (sin nota) o 1–5
 * @param {(n: number) => void} [onChange] — recibe 1–5 al elegir surco
 * @param {boolean} [readOnly=false]
 * @param {'sm'|'md'|'lg'} [size='md']
 * @param {number} [sizePx] — si se indica (>0), ancho/alto del SVG en px (p. ej. igual al avatar del perfil)
 * @param {boolean} [showLabel=false]
 * @param {number} [count] — número de valoraciones agregadas (texto opcional)
 */
export default function SurcosRating({
  value = 0,
  onChange,
  readOnly = false,
  size = 'md',
  sizePx,
  showLabel = false,
  count,
}) {
  const svgRef = useRef(null);
  const [hoverScore, setHoverScore] = useState(null);
  const reactId = useId().replace(/:/g, '');
  const gradId = `sr-grad-${reactId}`;
  const glowId = `sr-glow-${reactId}`;

  const px =
    typeof sizePx === 'number' && Number.isFinite(sizePx) && sizePx > 0
      ? Math.round(sizePx)
      : SIZE_PX[size] ?? SIZE_PX.md;
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  const display = readOnly ? v : hoverScore != null ? hoverScore : v;
  /** Vista “disco de oro”: los 5 surcos activos (valor o previsualización). */
  const goldRecordLook = display === 5;

  const handleMove = useCallback(
    e => {
      if (readOnly) return;
      const s = clientToScore(svgRef.current, e.clientX, e.clientY);
      setHoverScore(s);
    },
    [readOnly]
  );

  const handleLeave = useCallback(() => {
    if (readOnly) return;
    setHoverScore(null);
  }, [readOnly]);

  const handleClick = useCallback(
    e => {
      if (readOnly || !onChange) return;
      const s = clientToScore(svgRef.current, e.clientX, e.clientY);
      if (s != null) onChange(s);
    },
    [readOnly, onChange]
  );

  const labelPrimary = useMemo(() => {
    if (display < 1) return 'Sin surcos';
    if (display === 1) return '1 surco';
    if (display === 5) return '5 surcos';
    return `${display} surcos`;
  }, [display]);

  const labelSecondary = display >= 1 && display <= 4 ? QUALITY[display] : display === 5 ? 'Disco de Oro' : '';

  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: showLabel || count != null ? 8 : 0,
      }}
    >
      <svg
        ref={svgRef}
        role="img"
        aria-label={readOnly ? `Valoración: ${v} de 5 surcos` : 'Valoración por surcos: pasa el cursor o pulsa sobre los surcos del centro al exterior (1 a 5)'}
        tabIndex={readOnly ? undefined : 0}
        width={px}
        height={px}
        viewBox="0 0 100 100"
        style={{
          cursor: readOnly ? 'default' : 'pointer',
          display: 'block',
          userSelect: 'none',
          outline: 'none',
        }}
        onKeyDown={
          readOnly || !onChange
            ? undefined
            : e => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const next = Math.min(5, Math.max(1, (v || 0) + 1));
                  onChange(next);
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  onChange(Math.max(1, (v || 1) - 1));
                }
              }
        }
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        onClick={handleClick}
      >
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f0e0a0" />
            <stop offset="50%" stopColor="#c8a84b" />
            <stop offset="100%" stopColor="#7a5c1e" />
          </linearGradient>
          <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="1.1" floodColor="#c8a84b" floodOpacity="0.45" />
          </filter>
        </defs>

        {goldRecordLook && (
          <circle cx="50" cy="50" r="48.5" fill={DISC_GOLD_BG} stroke={`url(#${gradId})`} strokeWidth="1.2" />
        )}

        {!goldRecordLook && <circle cx="50" cy="50" r="48.5" fill="#0d0d12" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />}

        {GROOVE_R.map((r, i) => {
          const lit = display > i;
          let stroke;
          let filterUrl;
          if (!lit) {
            stroke = GROOVE_OFF;
          } else if (goldRecordLook) {
            stroke = `url(#${gradId})`;
            filterUrl = undefined;
          } else {
            stroke = GOLD;
            filterUrl = `url(#${glowId})`;
          }
          return (
            <circle
              key={r}
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke={stroke}
              strokeWidth={STROKE}
              filter={filterUrl}
              style={{ transition: 'stroke 0.18s ease' }}
            />
          );
        })}

        <circle cx="50" cy="50" r={HOLE_R} fill="#07070a" stroke="rgba(255,255,255,0.12)" strokeWidth="0.35" />
      </svg>

      {(showLabel || count != null) && (
        <div style={{ textAlign: 'center', maxWidth: px + 80 }}>
          {showLabel && (
            <>
              <div style={{ fontSize: size === 'lg' ? 13 : size === 'md' ? 12 : 10, fontWeight: 700, color: display === 5 ? GOLD : 'rgba(255,255,255,0.82)', fontFamily: 'Georgia, serif' }}>
                {display === 5 ? '✨ 5 surcos' : labelPrimary}
              </div>
              {labelSecondary ? (
                <div style={{ fontSize: size === 'lg' ? 11 : 10, color: display === 5 ? 'rgba(240,224,160,0.8)' : 'rgba(255,255,255,0.45)', marginTop: 3 }}>{labelSecondary}</div>
              ) : null}
            </>
          )}
          {count != null && Number(count) >= 0 && (
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: showLabel ? 6 : 0 }}>
              {Number(count) === 1 ? '1 valoración' : `${count} valoraciones`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
